import { RouteRecordRaw } from 'vue-router';
import app from '@/main';
import { IRouteConfigAuthMeta } from '@/shared';

const auth: IRouteConfigAuthMeta = {
  required: true,
};

const settings: RouteRecordRaw = {
  path: '/settings',
  name: 'Account Settings',
  redirect: '/settings/company',
  meta: {
    auth,
    title: 'Account Settings',
    icon: 'cogs',
    active: false,
  },
};

const accountCustomFields: RouteRecordRaw = {
  path: '/settings/account-custom-fields',
  name: 'SettingsAccountCustomFields',
  component: () => import('@/views/websites/single/global-content/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['custom_fields'],
    },
    title: 'Account Custom Fields',
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => [
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const superAdminSettings: RouteRecordRaw = {
  path: '/superadmin-account-settings',
  name: 'SuperadminAccountSettings',
  redirect: '/superadmin-account-settings/global',
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Superadmin Account Settings',
    icon: 'cogs',
    active: false,
    parent: settings,
  },
};

const userManagement: RouteRecordRaw = {
  path: '/settings/user-management',
  name: 'SettingsUserManagement',
  redirect: '/settings/user-management/user-groups',
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Management',
    icon: 'users',
    active: false,
    noPagePadding: true,
  },
};

const userGroups: RouteRecordRaw = {
  path: '/settings/user-management/user-groups',
  name: 'SettingsUserGroups',
  component: () => import('@/views/settings/user-management/user-groups/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Groups',
    icon: 'users',
    active: false,
    parent: userManagement,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const userGroupsSingle: RouteRecordRaw = {
  path: '/settings/user-management/user-groups/:userGroupId/details',
  name: 'SettingsUserGroupsSingle',
  component: () => import('@/views/settings/user-management/user-groups/single/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Group Details',
    active: false,
    useContentBg: true,
    parent: userGroups,
    backButtonTarget: () => ({ target: userGroups, isCustom: false }),
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: '/settings/user-management/user-groups',
        title: 'User Groups',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const users: RouteRecordRaw = {
  path: '/settings/user-management/users',
  name: 'SettingsUsers',
  component: () => import('@/views/settings/user-management/users/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'Users',
    active: false,
    parent: userManagement,
    noPagePadding: true,
    icon: 'users',
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: '/settings/user-management/users',
        title: 'Users',
      },
    ],
  },
};

const usersSingle: RouteRecordRaw = {
  path: '/settings/user-management/users/:userId/details',
  name: 'SettingsUsersSingle',
  component: () => import('@/views/settings/user-management/users/single/IndexV2.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['client_settings'],
    },
    title: 'User Details',
    active: false,
    parent: users,
    backButtonTarget: () => ({ isCustom: false, target: users }),
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasDelete: true,
      hasCustomText: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/settings/user-management',
        title: 'User Management',
      },
      {
        to: '/settings/user-management/users',
        title: 'Users',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ],
  },
};

const panelTemplates: RouteRecordRaw = {
  path: '/settings/panels',
  name: 'SettingsPanels',
  component: () => import('@/views/settings/panel-templates/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    noPagePadding: true,
    title: 'Panels',
    hasOwnScrollbar: true,
    icon: 'cogs',
    active: false,
    parent: settings,
  },
};

const panelTemplateSingle: RouteRecordRaw = {
  path: '/settings/panels/:id/details',
  name: 'SettingsPanelTemplateSingle',
  component: () => import('@/views/settings/panel-templates/single/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Edit Panel Template',
    hidePageTitle: true,
    hasOwnScrollbar: true,
    noPagePadding: true,
    icon: 'cogs',
    active: false,
    parent: panelTemplates,
  },
};

const company: RouteRecordRaw = {
  path: '/settings/company',
  name: 'SettingsCompany',
  component: () => import('@/views/settings/CompanyProfile.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['company'],
    },
    title: 'Company Profile',
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => [
      {
        to: '/settings/company',
        title: 'Company Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const contact: RouteRecordRaw = {
  path: '/settings/contact',
  name: 'SettingsContact',
  component: () => import('@/views/settings/ContactDetails.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['company'],
    },
    title: 'Contact Details',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
      hasLastUpdated: true,
      hasCheckBox: true,
    },
    breadcrumbs: (vm) => [
      {
        to: '/settings/company',
        title: 'Company Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const billing: RouteRecordRaw = {
  path: '/settings/billing',
  name: 'SettingsBilling',
  component: () => import('@/views/settings/BillingReports.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['accounting'],
    },
    title: 'Billing',
    icon: 'cogs',
    active: false,
    parent: settings,
    dynamicHideFromNav: () => window.APP_CONFIG.VUE_APP_ENV === 'demo',
  },
};

const invoices: RouteRecordRaw = {
  path: '/settings/invoices',
  name: 'SettingsInvoices',
  component: () => import('@/views/settings/Invoices.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['accounting'],
    },
    title: 'Invoices',
    icon: 'cogs',
    active: false,
    parent: settings,
    infoUrl: 'y',
    dynamicHideFromNav: () => window.APP_CONFIG.VUE_APP_ENV === 'demo',
  },
};

const billingLegacy: RouteRecordRaw = {
  path: '/settings/billing-legacy',
  name: 'SettingsBillingLegacy',
  component: () => import('@/views/settings/BillingReportsLegacy.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Billing (Legacy)',
    icon: 'cogs',
    active: false,
    parent: settings,
    dynamicHideFromNav: () => true,
    breadcrumbs: (vm) => [
      {
        to: '/settings/billingBeta',
        title: 'Accounting',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const accounting: RouteRecordRaw = {
  path: '/settings/accounting',
  name: 'SettingsAccounting',
  component: () => import('@/views/settings/Accounting.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['accounting'],
    },
    title: 'Accounting Overview',
    icon: 'cogs',
    active: false,
    parent: settings,
    noPagePadding: true,
    breadcrumbs: (vm) => [
      {
        to: '/settings/billingBeta',
        title: 'Accounting',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const invoicing: RouteRecordRaw = {
  path: '/settings/invoicing',
  name: 'SettingsInvoicing',
  component: () => import('@/views/settings/Invoicing.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Invoicing',
    icon: 'cogs',
    active: false,
    parent: settings,
    dynamicHideFromNav: () => window.APP_CONFIG.VUE_APP_ENV === 'demo',
    breadcrumbs: (vm) => [
      {
        to: '/settings/billingBeta',
        title: 'Accounting',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ],
  },
};

const superadminAll: RouteRecordRaw = {
  path: '/superadmin-account-settings/all-settings',
  name: 'SuperAdminSettingsAll',
  component: () => import('@/views/superadmin-settings/AllSettings.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'All Settings',
    hideFromNav: true,
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
  },
};

const superadminIntegrations: RouteRecordRaw = {
  path: '/superadmin-account-settings/integrations',
  name: 'SuperAdminSettingsIntegrations',
  component: () => import('@/views/superadmin-settings/Integrations.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Integrations',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/integrations',
        title: 'Integrations',
      },
    ]),
  },
};

const superadminLanguage: RouteRecordRaw = {
  name: 'SuperAdminLanguageSettings',
  path: '/superadmin-account-settings/languages',
  component: () => import('@/views/superadmin-settings/Language.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Language Settings',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    dynamicHideFromNav: () => !!app()?.$rsGlobals.$currentClient.parentId,
    footerBar: {
      enabled: true,
      hasSave: true,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const superadminModules: RouteRecordRaw = {
  path: '/superadmin-account-settings/modules',
  name: 'SuperAdminSettingsModules',
  /**
   * Determine component loaded for this route based on ENV variable.
   * Set as true for testing.
   */
  component: () => import(`@/views/superadmin-settings/ModulesV2.vue`),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Modules',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const superAdminPanelTemplates: RouteRecordRaw = {
  path: '/superadmin-account-settings/panel-templates',
  name: 'SuperAdminSettingsPanelTemplates',
  component: () => import(/* webpackChunkName: "group-settings-panels" */ '@/views/settings/panel-templates/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    noPagePadding: true,
    title: 'Panel Templates',
    hasOwnScrollbar: true,
    icon: 'cogs',
    active: false,
    parent: settings,
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: vm?.$route.path ?? '',
        title: vm?.$route.meta?.title ?? '',
      },
    ]),
  },
};

const superAdminPanelTemplateSingle: RouteRecordRaw = {
  path: '/superadmin-account-settings/panel-templates/:id/details',
  name: 'SuperAdminSettingsPanelTemplateSingle',
  component: () => import(/* webpackChunkName: "group-settings-panels" */ '@/views/settings/panel-templates/single/Index.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Edit Panel Template',
    hidePageTitle: true,
    hasOwnScrollbar: true,
    noPagePadding: true,
    icon: 'cogs',
    active: false,
    backButtonTarget: () => ({ isCustom: false, target: superAdminPanelTemplates }),
    breadcrumbs: (vm) => ([
      {
        to: '/superadmin-account-settings/languages',
        title: 'Super Admin Settings',
      },
      {
        to: '/superadmin-account-settings/panel-templates',
        title: 'Panel Templates',
      },
      {
        to: vm?.$route.fullPath ?? '',
        title: 'Details',
      },
    ]),
  },
};

const superAdminBuildingStackSettings: RouteRecordRaw = {
  path: '/superadmin-account-settings/bstk-settings',
  name: 'SuperAdminBuildingStackSettings',
  component: () => import('@/views/superadmin-settings/BuildingStackSettings.vue'),
  meta: {
    auth: {
      required: true,
      permissions: ['superuser'],
    },
    title: 'Building Stack Settings',
    icon: 'cogs',
    active: false,
    parent: superAdminSettings,
    footerBar: {
      enabled: true,
      hasSave: true,
      hasTranslations: false,
    },
  },
};

export default [
  settings,
  company,
  contact,
  accountCustomFields,
  userManagement,
  userGroups,
  userGroupsSingle,
  users,
  usersSingle,
  billing,
  invoices,
  billingLegacy,
  accounting,
  invoicing,
  panelTemplates,
  panelTemplateSingle,
  superAdminSettings,
  superadminLanguage,
  superadminAll,
  superadminModules,
  superadminIntegrations,
  superAdminPanelTemplates,
  superAdminPanelTemplateSingle,
  superAdminBuildingStackSettings,
] as Array<RouteRecordRaw>;
